'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Link from 'next/link';
import {
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Upload,
  X,
  Info,
  FilePlus2,
} from 'lucide-react';
import { useToast } from '@/components/ui/Toast';

interface FundRequestFormValues {
  requestorFirstName: string;
  requestorLastName: string;
  clientName: string;
  customerProject: string;
  expenseType: string;
  amountRequested: string;
  status: string;
  bankReference: string;
  releaseDate: string;
  notes: string;
}

const expenseTypes = ['Labour', 'Material', 'Fuel', 'Transport', 'Accommodation'];
const statusOptions = ['Submitted', 'Approved', 'Released'];

export default function ProjectFundRequestFormContent() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [generatedId] = useState(() => {
    const now = new Date();
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    return `FR-${dateStr}-${String(841023 + Math.floor(now.getSeconds() * 100)).slice(0, 6)}`;
  });
  const [attachedFiles, setAttachedFiles] = useState<string[]>([]);
  const [dragOver, setDragOver] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FundRequestFormValues>({
    defaultValues: {
      status: 'Submitted',
    },
  });

  const watchStatus = watch('status');

  const onSubmit = async (_data: FundRequestFormValues) => {
    setIsSubmitting(true);
    // Backend integration point: POST /api/fund-requests
    await new Promise((r) => setTimeout(r, 1400));
    setIsSubmitting(false);
    setIsSuccess(true);
    toast('success', 'Fund request submitted', `Request ID ${generatedId} created successfully.`);
    setTimeout(() => setIsSuccess(false), 3000);
    reset();
    setAttachedFiles([]);
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).map((f) => f.name);
    setAttachedFiles((prev) => [...prev, ...files].slice(0, 10));
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files).map((f) => f.name);
    setAttachedFiles((prev) => [...prev, ...files].slice(0, 10));
  };

  const removeFile = (name: string) => {
    setAttachedFiles((prev) => prev.filter((f) => f !== name));
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link
          href="/"
          className="text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-md hover:bg-muted"
        >
          <ArrowLeft size={18} />
        </Link>
        <div>
          <h1 className="text-2xl font-700 text-foreground">New Fund Request</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Submit a project expense request for EXCO approval
          </p>
        </div>
      </div>

      {/* Auto-generated ID banner */}
      <div className="flex items-start gap-2.5 bg-info-bg border border-info/20 rounded-lg px-4 py-3 mb-6">
        <Info size={15} className="text-info mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-600 text-foreground">Auto-generated Request ID</p>
          <p className="text-sm text-muted-foreground">
            Your request will be assigned ID:{' '}
            <span className="font-700 text-foreground tabular-nums">{generatedId}</span>
          </p>
        </div>
      </div>

      {isSuccess && (
        <div className="flex items-center gap-2.5 bg-success-bg border border-success/20 rounded-lg px-4 py-3 mb-6 fade-in">
          <CheckCircle2 size={16} className="text-success shrink-0" />
          <p className="text-sm font-600 text-success">Data Added Successfully! Your request has been submitted.</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="bg-card border border-border rounded-xl shadow-card divide-y divide-border">
          {/* Section: Requestor Details */}
          <div className="px-5 py-4">
            <h2 className="text-sm font-700 text-foreground mb-4">Requestor Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* First Name */}
              <div>
                <label className="block text-xs font-600 text-foreground mb-1" htmlFor="requestorFirstName">
                  First Name <span className="text-danger">*</span>
                </label>
                <input
                  id="requestorFirstName"
                  type="text"
                  placeholder="e.g. Amara"
                  className={`w-full rounded-md border px-3 py-2 text-sm text-foreground bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow ${
                    errors.requestorFirstName ? 'border-danger focus:ring-danger/30' : 'border-input'
                  }`}
                  {...register('requestorFirstName', { required: 'First name is required' })}
                />
                {errors.requestorFirstName && (
                  <p className="text-xs text-danger mt-1">{errors.requestorFirstName.message}</p>
                )}
              </div>
              {/* Last Name */}
              <div>
                <label className="block text-xs font-600 text-foreground mb-1" htmlFor="requestorLastName">
                  Last Name <span className="text-danger">*</span>
                </label>
                <input
                  id="requestorLastName"
                  type="text"
                  placeholder="e.g. Nkosi"
                  className={`w-full rounded-md border px-3 py-2 text-sm text-foreground bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow ${
                    errors.requestorLastName ? 'border-danger focus:ring-danger/30' : 'border-input'
                  }`}
                  {...register('requestorLastName', { required: 'Last name is required' })}
                />
                {errors.requestorLastName && (
                  <p className="text-xs text-danger mt-1">{errors.requestorLastName.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Section: Project Details */}
          <div className="px-5 py-4">
            <h2 className="text-sm font-700 text-foreground mb-4">Project Details</h2>
            <div className="space-y-4">
              {/* Client Name */}
              <div>
                <label className="block text-xs font-600 text-foreground mb-1" htmlFor="clientName">
                  Client Name <span className="text-danger">*</span>
                </label>
                <input
                  id="clientName"
                  type="text"
                  placeholder="e.g. TanzaPort Authority"
                  className={`w-full rounded-md border px-3 py-2 text-sm text-foreground bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow ${
                    errors.clientName ? 'border-danger focus:ring-danger/30' : 'border-input'
                  }`}
                  {...register('clientName', { required: 'Client name is required' })}
                />
                {errors.clientName && (
                  <p className="text-xs text-danger mt-1">{errors.clientName.message}</p>
                )}
              </div>
              {/* Customer Project */}
              <div>
                <label className="block text-xs font-600 text-foreground mb-1" htmlFor="customerProject">
                  Customer Project <span className="text-danger">*</span>
                </label>
                <input
                  id="customerProject"
                  type="text"
                  placeholder="e.g. Port Expansion Phase 2"
                  className={`w-full rounded-md border px-3 py-2 text-sm text-foreground bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow ${
                    errors.customerProject ? 'border-danger focus:ring-danger/30' : 'border-input'
                  }`}
                  {...register('customerProject', { required: 'Customer project is required' })}
                />
                {errors.customerProject && (
                  <p className="text-xs text-danger mt-1">{errors.customerProject.message}</p>
                )}
              </div>
              {/* Expense Type */}
              <div>
                <label className="block text-xs font-600 text-foreground mb-1" htmlFor="expenseType">
                  Expense Type <span className="text-danger">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Select the category that best describes this expense</p>
                <select
                  id="expenseType"
                  className={`w-full rounded-md border px-3 py-2 text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-ring transition-shadow ${
                    errors.expenseType ? 'border-danger focus:ring-danger/30' : 'border-input'
                  }`}
                  {...register('expenseType', { required: 'Expense type is required' })}
                >
                  <option value="">Select expense type…</option>
                  {expenseTypes.map((et) => (
                    <option key={`expense-${et}`} value={et}>{et}</option>
                  ))}
                </select>
                {errors.expenseType && (
                  <p className="text-xs text-danger mt-1">{errors.expenseType.message}</p>
                )}
              </div>
              {/* Amount */}
              <div>
                <label className="block text-xs font-600 text-foreground mb-1" htmlFor="amountRequested">
                  Amount Requested (TZS) <span className="text-danger">*</span>
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Enter amount in Tanzanian Shillings</p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-600 text-muted-foreground">TZS</span>
                  <input
                    id="amountRequested"
                    type="number"
                    placeholder="0"
                    className={`w-full rounded-md border pl-12 pr-3 py-2 text-sm text-foreground bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow tabular-nums ${
                      errors.amountRequested ? 'border-danger focus:ring-danger/30' : 'border-input'
                    }`}
                    {...register('amountRequested', {
                      required: 'Amount is required',
                      min: { value: 1, message: 'Amount must be greater than 0' },
                    })}
                  />
                </div>
                {errors.amountRequested && (
                  <p className="text-xs text-danger mt-1">{errors.amountRequested.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Section: Attachments */}
          <div className="px-5 py-4">
            <h2 className="text-sm font-700 text-foreground mb-1">Attachments</h2>
            <p className="text-xs text-muted-foreground mb-3">Upload supporting documents — up to 10 files</p>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleFileDrop}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-150 ${
                dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'
              }`}
            >
              <Upload size={20} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-foreground font-500">Drop files here or{' '}
                <label className="text-primary cursor-pointer hover:underline">
                  browse
                  <input type="file" multiple className="hidden" onChange={handleFileInput} />
                </label>
              </p>
              <p className="text-xs text-muted-foreground mt-1">PDF, DOCX, XLSX, JPG, PNG up to 10MB each</p>
            </div>
            {attachedFiles.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {attachedFiles.map((file) => (
                  <li key={`file-${file}`} className="flex items-center justify-between bg-muted rounded-md px-3 py-2">
                    <span className="text-xs text-foreground truncate max-w-[80%]">{file}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(file)}
                      className="text-muted-foreground hover:text-danger transition-colors"
                    >
                      <X size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Section: Approval Status */}
          <div className="px-5 py-4">
            <h2 className="text-sm font-700 text-foreground mb-4">Approval Status</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Status */}
              <div>
                <label className="block text-xs font-600 text-foreground mb-1" htmlFor="status">
                  Status
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Automatically set to Submitted on creation</p>
                <select
                  id="status"
                  className="w-full rounded-md border border-input px-3 py-2 text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-ring transition-shadow"
                  {...register('status')}
                >
                  {statusOptions.map((s) => (
                    <option key={`status-${s}`} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              {/* Bank Reference — only relevant when Released */}
              <div>
                <label className="block text-xs font-600 text-foreground mb-1" htmlFor="bankReference">
                  Bank Reference
                  {watchStatus === 'Released' && <span className="text-danger ml-1">*</span>}
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Required when status is Released</p>
                <input
                  id="bankReference"
                  type="text"
                  placeholder="e.g. CRDB-2026-44821"
                  className={`w-full rounded-md border px-3 py-2 text-sm text-foreground bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow ${
                    errors.bankReference ? 'border-danger focus:ring-danger/30' : 'border-input'
                  } ${watchStatus !== 'Released' ? 'opacity-50' : ''}`}
                  disabled={watchStatus !== 'Released'}
                  {...register('bankReference', {
                    validate: (val) => {
                      if (watchStatus === 'Released' && !val) return 'Bank reference required when releasing funds';
                      return true;
                    },
                  })}
                />
                {errors.bankReference && (
                  <p className="text-xs text-danger mt-1">{errors.bankReference.message}</p>
                )}
              </div>
              {/* Release Date */}
              <div>
                <label className="block text-xs font-600 text-foreground mb-1" htmlFor="releaseDate">
                  Release Date
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Date funds were or will be released</p>
                <input
                  id="releaseDate"
                  type="date"
                  className="w-full rounded-md border border-input px-3 py-2 text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-ring transition-shadow"
                  {...register('releaseDate')}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sticky footer */}
        <div className="sticky bottom-0 mt-4 bg-background/95 backdrop-blur border-t border-border py-3 flex items-center justify-between gap-3 -mx-6 px-6 lg:-mx-8 lg:px-8 xl:-mx-10 xl:px-10 2xl:-mx-12 2xl:px-12">
          <Link
            href="/"
            className="px-4 py-2 rounded-md border border-border text-sm font-600 text-foreground hover:bg-muted transition-all duration-150 active:scale-95"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-primary text-primary-foreground text-sm font-600 hover:bg-primary/90 transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed min-w-[140px] justify-center"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Submitting…
              </>
            ) : (
              <>
                <FilePlus2 size={14} />
                Submit Request
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}